/**
 * Helper functions for prediction display and formatting
 */

import { BasePick, EntityTeam, Team, PickTopic, PickEntity, ModelPick } from '../types/ModelPicks';
import { EPick } from '../types/EPicks';
import type { BetFormat } from '../types/Outcome';
import type { Theme } from '@mui/material/styles';

// Interface for model information display
export interface ModelInfo {
  id: string;
  name: string;
  roi100: number;
  winRate: number;
  totalPredictions: number;
}

/**
 * Format game date and time for display
 */
export const formatGameTime = (dateString: string): string => {
  const date = new Date(dateString);
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const timeStr = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
  return `${month}/${day} • ${timeStr}`;
};

/**
 * Maps bet strength to pick strength display value
 * Model-specific logic for determining pick strength
 *
 * #PICKCLASSIFICATION
 */
export const mapBetStrengthToPickStrength = (betStrength: number, betAction: string): 'strong' | 'normal' | 'weak' | 'none' => {
  if (betAction === 'no_bet') {
    return 'none';
  }

  if (betStrength >= 2) {
    return 'strong';
  } else if (betStrength >= 1) {
    return 'normal';
  } else if (betStrength >= 0.5) {
    return 'weak';
  }

  return 'none';
};

/**
 * Get color based on recommendation strength
 */
export const getRecommendationColor = (recommendation: string, theme: Theme): string => {
  switch (recommendation) {
    case 'strong':
      return theme.recommendationColors.strong;
    case 'normal':
      return theme.recommendationColors.normal;
    case 'weak':
      return theme.recommendationColors.weak;
    case 'none':
      return theme.recommendationColors.none;
    default:
      return theme.recommendationColors.none;
  }
};

export const getRecommendationLabel = (recommendation: string) => {
  switch (recommendation) {
    case 'strong': return 'STRONG';
    case 'normal': return 'NORMAL';
    case 'weak': return 'WEAK';
    case 'none': return 'NO BET';
    default: return recommendation.toUpperCase();
  }
};
/**
 * Get color based on ROI value
 */
export const getROIColor = (roi: number, theme: Theme): string => {
  if (roi > 2) return theme.colors.green; // green
  if (roi >= 0) return theme.colors.orange; // orange
  return '#F44336'; // red
};

/**
 * Get color based on expected value
 */
export const getEVColor = (ev: number, theme: Theme): string => {
  if (ev >= 5) return theme.colors.green;
  if (ev >= 2.5) return theme.colors.orange;
  if (ev >= 0) return theme.colors.yellow;
  return theme.colors.red;
};

/**
 * Convert American odds to decimal odds
 */
const americanToDecimal = (americanOdds: number): number => {
  if (americanOdds > 0) {
    return (americanOdds / 100) + 1;
  } else {
    return (100 / Math.abs(americanOdds)) + 1;
  }
};

/**
 * Convert American odds to implied probability percentage
 */
export const americanToImplied = (americanOdds: number): number => {
  if (americanOdds > 0) {
    return (100 / (americanOdds + 100)) * 100;
  } else {
    return (Math.abs(americanOdds) / (Math.abs(americanOdds) + 100)) * 100;
  }
};

/**
 * Convert decimal to simplified fraction
 */
const decimalToFraction = (decimal: number): string => {
  // Get the fractional part (subtract 1 since decimal odds include the stake)
  const fractional = decimal - 1;

  // Handle edge cases
  if (fractional <= 0) return '0/1';

  // Find greatest common divisor
  const gcd = (a: number, b: number): number => {
    return b === 0 ? a : gcd(b, a % b);
  };

  // Convert to fraction (multiply by 100 to handle decimals, then simplify)
  const precision = 100;
  let numerator = Math.round(fractional * precision);
  let denominator = precision;

  // Simplify the fraction
  const divisor = gcd(numerator, denominator);
  numerator = numerator / divisor;
  denominator = denominator / divisor;

  return `${numerator}/${denominator}`;
};

/**
 * Format odds with proper +/- sign (American format by default)
 * @param odds American odds value
 * @param format Optional format: 'american' | 'decimal' | 'fractional' | 'implied' | 'fixed'
 * @returns Object with display value and qualifier text ("or better" / "or less")
 */
export const formatOdds = (odds: number, format: string = 'american'): { display: string; qualifier: string } => {
  switch (format) {
    case 'decimal': {
      const decimal = americanToDecimal(odds);
      return {
        display: decimal.toFixed(2),
        qualifier: 'or better'
      };
    }

    case 'fractional': {
      const decimal = americanToDecimal(odds);
      return {
        display: decimalToFraction(decimal),
        qualifier: 'or better'
      };
    }

    case 'implied': {
      const implied = americanToImplied(odds);
      return {
        display: `${implied.toFixed(1)}%`,
        qualifier: 'or better'
      };
    }

    case 'fixed': {
      const implied = americanToImplied(odds);
      const payout = implied / 100;
      return {
        display: `$${payout.toFixed(2)}`,
        qualifier: 'or less'
      };
    }

    case 'american':
    default:
      return {
        display: odds > 0 ? `+${odds}` : `${odds}`,
        qualifier: 'or better'
      };
  }
};

/**
 * Determine the outcome of a pick based on the result
 * Returns 'win', 'loss', 'push', or null (no result yet)
 * Uses betFormat to determine calculation logic (with fallback to predictionType for migration)
 */
export const determinePickOutcome = (pick: BasePick): 'win' | 'loss' | 'push' | null => {
  // Player prop picks: only use topicResult, never fall back to entityResult
  if (pick.outcomeType === 'player_prop') {
    // No result yet if topicResult is undefined
    if (pick.topicResult === undefined) {
      return null;
    }

    const line = pick.point;
    if (line === undefined || line === null) {
      return null;
    }

    // Push if exactly on the line
    if (pick.topicResult === line) {
      return 'push';
    }

    if (pick.betAction === 'bet_over') {
      return pick.topicResult > line ? 'win' : 'loss';
    } else if (pick.betAction === 'bet_under') {
      return pick.topicResult < line ? 'win' : 'loss';
    }

    return null;
  }

  // Team picks: use entityResult (game scores)
  if (!pick.entityResult) {
    return null;
  }

  // Ensure entityResult is an object with home/away scores
  if (typeof pick.entityResult !== 'object') {
    return null;
  }

  const homeScore = pick.entityResult.home;
  const awayScore = pick.entityResult.away;

  // Get bet format (with fallback to inference from outcomeId)
  const betFormat = getBetFormatWithFallback(pick.betFormat, pick.outcomeId);

  switch (betFormat) {
    case 'side': {
      // Moneyline - pick winner, no point spread
      const homeWon = homeScore > awayScore;
      const awayWon = awayScore > homeScore;
      if (homeScore === awayScore) return 'push';
      if (pick.betAction === 'bet_home') return homeWon ? 'win' : 'loss';
      if (pick.betAction === 'bet_away') return awayWon ? 'win' : 'loss';
      return null;
    }

    case 'side_spread': {
      // Spread - pick with point handicap
      const spread = pick.point ?? 0;
      if (pick.betAction === 'bet_home') {
        const adjustedHomeScore = homeScore + spread;
        if (adjustedHomeScore === awayScore) return 'push';
        return adjustedHomeScore > awayScore ? 'win' : 'loss';
      }
      if (pick.betAction === 'bet_away') {
        const adjustedAwayScore = awayScore - spread;
        if (adjustedAwayScore === homeScore) return 'push';
        return adjustedAwayScore > homeScore ? 'win' : 'loss';
      }
      return null;
    }

    case 'over_under': {
      // Total - over/under a number
      const totalScore = homeScore + awayScore;
      const line = pick.point ?? 0;
      if (totalScore === line) return 'push';
      if (pick.betAction === 'bet_over') return totalScore > line ? 'win' : 'loss';
      if (pick.betAction === 'bet_under') return totalScore < line ? 'win' : 'loss';
      return null;
    }

    case 'binary': {
      // Yes/no proposition - TODO: needs specific result handling
      // Binary team outcomes would need a different result structure
      return null;
    }

    default:
      return null;
  }
};

/**
 * Get formatted result display for a pick
 */
export const getResultDisplay = (
  pick: Pick<BasePick, 'awayTeam' | 'homeTeam' | 'outcomeId' | 'outcomeType' | 'entityResult' | 'topicResult'>
): string => {
  // Player prop picks: only show topicResult, never fall back to entityResult
  if (pick.outcomeType === 'player_prop') {
    if (pick.topicResult === undefined) {
      return '';
    }
    return `Actual: ${pick.topicResult}`;
  }

  // Team picks: show game score
  if (!pick.entityResult || typeof pick.entityResult !== 'object') {
    return '';
  }

  const homeScore = pick.entityResult.home;
  const awayScore = pick.entityResult.away;

  if (pick.outcomeId === 'ocm.total') {
    const totalScore = homeScore + awayScore;
    return `${pick.awayTeam.shortName} ${awayScore} - ${pick.homeTeam.shortName} ${homeScore} (Total: ${totalScore})`;
  }

  return `${pick.awayTeam.shortName} ${awayScore} - ${pick.homeTeam.shortName} ${homeScore}`;
};

// ============================================================================
// Shared Helper Functions for Mappers
// ============================================================================

/**
 * Formats a color string to ensure it has a # prefix
 */
export const formatColor = (color: string | undefined): string => {
  if (!color) return '#000000';
  return color.startsWith('#') ? color : `#${color}`;
};

/**
 * Maps an EntityTeam from the API to a Team for display
 * Returns null if entity data is invalid or missing required fields
 */
export const mapEntityTeamToTeam = (entityTeam: EntityTeam | undefined | null): Team | null => {
  // Validate entity team exists
  if (!entityTeam) {
    console.warn('mapEntityTeamToTeam: entityTeam is null or undefined');
    return null;
  }

  // Validate required fields
  if (!entityTeam.id || !entityTeam.name || !entityTeam.teamCode) {
    console.warn('mapEntityTeamToTeam: missing required fields', {
      id: entityTeam.id,
      name: entityTeam.name,
      teamCode: entityTeam.teamCode
    });
    return null;
  }

  // Validate colors object exists
  if (!entityTeam.colors || typeof entityTeam.colors !== 'object') {
    console.warn('mapEntityTeamToTeam: colors object is missing or invalid', {
      teamId: entityTeam.id,
      teamName: entityTeam.name
    });
    return null;
  }

  // Use default colors if primary is missing
  const primaryColor = entityTeam.colors.primary || '#808080';
  const secondaryColor = entityTeam.colors.secondary || '#606060';

  return {
    id: entityTeam.id,
    name: entityTeam.name,
    shortName: entityTeam.teamCode,
    colors: {
      primary: formatColor(primaryColor),
      secondary: formatColor(secondaryColor)
    }
  };
};

/**
 * Determines the winner team based on bet action and bet format
 * Works for both model picks and ensemble picks
 */
export const determineWinnerTeam = (
  betAction: string,
  homeTeam: Team,
  awayTeam: Team,
  betFormat: BetFormat,
  options?: {
    topic?: PickTopic;
    entity?: PickEntity;
  }
): Team | null => {
  if (['binary', 'over_under'].includes(betFormat)) {
    // Over/under bets don't have a winner team
    return null;
  }

  if (betAction === 'bet_home') {
    return homeTeam;
  } else if (betAction === 'bet_away') {
    return awayTeam;
  } else if (betAction === 'no_bet' && options?.topic) {
    const topic = options.topic;

    // For ensemble picks, we need to check against the entity teams
    if (options.entity) {
      const homeEntityTeam = options.entity.home;
      const awayEntityTeam = options.entity.away;

      // Try to match topic with home or away team
      if (topic.id === homeEntityTeam.id ||
          (topic.codes?.nflTeamCode && topic.codes.nflTeamCode === homeEntityTeam.teamCode) ||
          ((topic as PickTopic).codes?.mlbTeamCode && (topic as PickTopic)?.codes?.mlbTeamCode === homeEntityTeam.teamCode)) {
        return homeTeam;
      } else if (topic.id === awayEntityTeam.id ||
                 (topic.codes?.nflTeamCode && topic.codes.nflTeamCode === awayEntityTeam.teamCode) ||
                 ((topic as PickTopic).codes?.mlbTeamCode && (topic as PickTopic)?.codes?.mlbTeamCode === awayEntityTeam.teamCode)) {
        return awayTeam;
      }
    } else {
      // For model picks, check against the team shortName
      if (topic.id === homeTeam.id ||
          (topic.codes?.nflTeamCode && topic.codes.nflTeamCode === homeTeam.shortName)) {
        return homeTeam;
      } else if (topic.id === awayTeam.id ||
                 (topic.codes?.nflTeamCode && topic.codes.nflTeamCode === awayTeam.shortName)) {
        return awayTeam;
      }
    }
  }

  // Default to null for no_bet without matching topic
  return null;
};

/**
 * Determine the result of a pick (win, loss, push, no-bet, or undefined)
 */
export type PickResult = 'win' | 'loss' | 'push' | 'no-bet' | undefined;

export const getPickResult = (pick: ModelPick | EPick): PickResult => {
  // Check if no result yet
  if (!pick.entityResult) {
    return undefined;
  }

  // Check if this is a no-bet scenario
  const isNoBet =
    ('recommendation' in pick && pick.recommendation === 'none') || // EPick
    ('pick' in pick && pick.pick === 'none'); // ModelPick

  if (isNoBet) {
    return 'no-bet';
  }

  // Determine win/loss/push
  const outcome = determinePickOutcome(pick);
  if (outcome === null) {
    return undefined; // Shouldn't happen if entityResult exists, but be safe
  }

  return outcome; // 'win', 'loss', or 'push'
};

/**
 * Get colors for card and footer based on pick result
 */
export interface ResultColors {
  cardBg?: string;
  footerBg: string;
}

export const getResultColors = (result: PickResult, theme: Theme): ResultColors => {
  switch (result) {
    case 'win':
      return {
        cardBg: undefined,
        footerBg: theme.colors.green + '20'
      };
    case 'loss':
      return {
        cardBg: undefined,
        footerBg: theme.colors.red + '20'
      };
    case 'push':
      return {
        cardBg: undefined,
        footerBg: theme.colors.orange + '20'
      };
    case 'no-bet':
    case undefined:
    default:
      return {
        cardBg: undefined,
        footerBg: theme.palette.action.hover
      };
  }
};

// ============================================================================
// Bet Format Utilities
// ============================================================================

/**
 * Infer bet format from outcomeId when betFormat is not set on the outcome
 * This provides backward compatibility until all outcomes have betFormat set
 */
export const inferBetFormat = (outcomeId: string): BetFormat => {
  if (outcomeId.includes('moneyline')) {
    return 'side';
  } else if (outcomeId.includes('spread')) {
    return 'side_spread';
  } else if (outcomeId.includes('total')) {
    return 'over_under';
  }
  // Default to over_under for player props and unknown outcomes
  return 'over_under';
};

/**
 * Format the point/line value for display based on bet format
 * Centralizes the formatting logic that was duplicated across 11+ files
 */
export const formatPickPoint = (
  point: number | undefined | null,
  betFormat: BetFormat,
  betAction?: string
): string | null => {
  if (point === undefined || point === null) return null;

  switch (betFormat) {
    case 'side':
      // Moneyline doesn't display a point value
      return null;

    case 'side_spread':
      // Spread: show with +/- prefix
      return `${point > 0 ? '+' : ''}${point}`;

    case 'over_under':
      // Over/under: show with O/U prefix
      if (betAction === 'bet_over') return `O ${point}`;
      if (betAction === 'bet_under') return `U ${point}`;
      return `${point}`;

    case 'binary':
      // Binary: show Yes/No
      if (betAction === 'bet_yes') return 'Yes';
      if (betAction === 'bet_no') return 'No';
      return null;

    default:
      return `${point}`;
  }
};

/**
 * Configuration for what UI elements to display based on bet format
 */
export interface PickDisplayConfig {
  showTeamBadge: boolean;
  showPointValue: boolean;
  showOverUnderIndicator: boolean;
  showBinaryIndicator: boolean;
}

/**
 * Get display configuration based on bet format
 * Determines which UI elements should be shown for a pick
 */
export const getPickDisplayConfig = (betFormat: BetFormat): PickDisplayConfig => ({
  showTeamBadge: betFormat === 'side' || betFormat === 'side_spread',
  showPointValue: betFormat === 'side_spread' || betFormat === 'over_under',
  showOverUnderIndicator: betFormat === 'over_under',
  showBinaryIndicator: betFormat === 'binary',
});

/**
 * Get the bet format for an outcome, with fallback to inference
 * Use this when you need a bet format but the outcome may not have one set
 */
export const getBetFormatWithFallback = (
  betFormat: BetFormat | undefined,
  outcomeId: string
): BetFormat => {
  return betFormat ?? inferBetFormat(outcomeId);
};

/**
 * Get data points multiplier from bet format and operation type
 * Replaces the predictionType-based logic
 */
export const getMultiplierFromBetFormat = (betFormat: BetFormat, operationType: string): number => {
  const isTest = operationType === 'experiment' || operationType === 'quick_train';

  if (isTest) {
    return betFormat === 'side' ? 5 : 6;
  } else if (operationType === 'train') {
    return betFormat === 'side' ? 14 : 12;
  }
  return 10;
};

// ============================================================================
// Kalshi URL Generation
// ============================================================================

/** Map team codes to Kalshi's format (handle mismatches) */
const mapToKalshiTeamCode = (code: string): string => {
  const kalshiCodeMap: Record<string, string> = {
    'jax': 'jac', // Jacksonville Jaguars
  };
  const lowerCode = code.toLowerCase();
  return kalshiCodeMap[lowerCode] || lowerCode;
};

/**
 * Generate Kalshi URL for a game pick
 * URL pattern: https://kalshi.com/markets/kxnflgame/professional-football-game/kxnflgame-{YYMMMDD}{away}{home}
 */
export const generateKalshiUrl = (pick: EPick): string | null => {
  if (pick.domainId !== 'dom.nfl') {
    return null;
  }

  const awayCode = pick.awayTeam.shortName ? mapToKalshiTeamCode(pick.awayTeam.shortName) : null;
  const homeCode = pick.homeTeam.shortName ? mapToKalshiTeamCode(pick.homeTeam.shortName) : null;

  if (!awayCode || !homeCode) return null;

  const gameDate = new Date(pick.gameDate);
  const day = gameDate.getDate().toString().padStart(2, '0');
  const monthNames = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
  const month = monthNames[gameDate.getMonth()];
  const year = gameDate.getFullYear().toString().slice(-2);

  const gameId = `kxnflgame-${year}${month}${day}${awayCode}${homeCode}`;
  return `https://kalshi.com/markets/kxnflgame/professional-football-game/${gameId}`;
};